import { forwardRef, useRef, useEffect, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'default' | 'large' | 'xlarge' | 'wide' | 'full';
  /** If true, enables modal behavior (focus trap, body scroll lock). Default false to allow interaction with portaled elements like Select dropdowns. */
  modal?: boolean;
}

const sizeClasses = {
  default: 'max-w-md',
  large: 'max-w-lg',
  xlarge: 'max-w-2xl',
  wide: 'max-w-3xl',
  full: 'max-w-6xl',
};

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  ({ open, onOpenChange, title, description, children, footer, size = 'default', modal = false }, ref) => {
    const openTimestamp = useRef(0);
    const mouseDownOnOverlay = useRef(false);

    useEffect(() => {
      if (open) openTimestamp.current = Date.now();
    }, [open]);

    const handleOverlayClick = (e: React.MouseEvent) => {
      // Ignore clicks within 100ms of opening to prevent race conditions with the triggering click
      if (Date.now() - openTimestamp.current < 100) return;
      // Only close if the click started and ended on the overlay itself
      if (e.target === e.currentTarget && mouseDownOnOverlay.current) {
        onOpenChange(false);
      }
      mouseDownOnOverlay.current = false;
    };

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
        <Dialog.Portal>
          {open && (
            <div
              className="fixed inset-0 z-[49] bg-dark-900/40 backdrop-blur-[2px] animate-in fade-in-0"
              onMouseDown={(e) => {
                mouseDownOnOverlay.current = e.target === e.currentTarget;
              }}
              onClick={handleOverlayClick}
            />
          )}
          <Dialog.Content
            ref={ref}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className={cn(
              'fixed right-0 top-0 z-50 h-full w-full bg-white shadow-xl',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
              'duration-300 ease-in-out',
              sizeClasses[size]
            )}
          >
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between border-b border-neutral-200 p-6 max-sm:p-4">
                <div className="min-w-0">
                  <Dialog.Title className="text-lg font-semibold text-dark-500">
                    {title}
                  </Dialog.Title>
                  {description && (
                    <Dialog.Description className="mt-1 text-sm text-neutral-500">
                      {description}
                    </Dialog.Description>
                  )}
                </div>
                <Dialog.Close
                  className={cn(
                    'rounded-lg p-1 text-neutral-400',
                    'hover:bg-neutral-100 hover:text-neutral-600',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    'transition-colors'
                  )}
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </Dialog.Close>
              </div>

              <div className="flex-1 overflow-y-auto p-6 max-sm:p-4">{children}</div>

              {footer && (
                <div className="border-t border-neutral-200 p-6 max-sm:p-4">{footer}</div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

Drawer.displayName = 'Drawer';

export { Drawer };
